import qs from "query-string";
import i18next from "src/i18n/config";
import RouteService from "src/services/routeService";
import { invalidateOnSuccess, transformErrorResponse } from "src/store/helpers";
import { ApiResponse, BasicApiResponse, PaginatedResponse } from "src/types";

import { BaseQueryFn, EndpointBuilder } from "@reduxjs/toolkit/query";
import { pageIndexOffset } from "@shared/table/Tanstack.utils";

import type { policiesBaseApi } from "./index";

type PoliciesBuilder = EndpointBuilder<BaseQueryFn, "Policies", "policiesApi">;
type PoliciesBaseApi = typeof policiesBaseApi;

type GetPoliciesParams = {
    workspaceId: number;
};

export const policiesPageSize = 200;

function getPolicies(builder: PoliciesBuilder) {
    return builder.query<PaginatedResponse<API.PolicyViewModel>, GetPoliciesParams>({
        providesTags: ["Policies"],

        queryFn: async ({ workspaceId }, _api, _extraOptions, baseQuery) => {
            const base = await RouteService.getPoliciesRoute();
            let list: API.PolicyViewModel[] = [];
            let pageIndex = pageIndexOffset;
            let page: PaginatedResponse<API.PolicyViewModel>;

            do {
                const params = { workspaceId, pageIndex, pageSize: policiesPageSize };
                // eslint-disable-next-line no-await-in-loop -- sequential pagination: each page depends on the previous count
                const result = await baseQuery({ url: base, params, paramsSerializer: () => qs.stringify(params) });
                if (result.error) {
                    return {
                        error: transformErrorResponse(result.error, i18next.t("common:error.policies.failedToGetPolicies")),
                    };
                }
                page = result.data as PaginatedResponse<API.PolicyViewModel>;
                list = list.concat(page.list);
                pageIndex += 1;
            } while (page.list.length === policiesPageSize && list.length < page.count);

            return { data: { ...page, list } };
        },
    });
}

type PolicyIdParam = {
    id: string;
};

function getPolicyById(builder: PoliciesBuilder) {
    return builder.query<ApiResponse<API.PolicyViewModel>, PolicyIdParam>({
        providesTags: ["Policies"],
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.policies.failedToGetPolicy")),
        query: async ({ id }) => {
            const base = await RouteService.getPoliciesRoute();
            return {
                url: `${base}${id}`,
            };
        },
    });
}

export type CreatePolicyPayload = {
    workspaceId: number;
    name: string;
    content: API.Policy[];
};

function createPolicy(builder: PoliciesBuilder) {
    return builder.mutation<ApiResponse<API.PolicyViewModel>, CreatePolicyPayload>({
        invalidatesTags: invalidateOnSuccess("Policies"),
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.policies.failedToCreatePolicy")),
        query: async ({ content, ...payload }) => {
            const base = await RouteService.getPoliciesRoute();
            return {
                url: base,
                method: "POST",
                data: { ...payload, content },
            };
        },
    });
}

export type UpdatePolicyPayload = PolicyIdParam & {
    name: string;
    content: API.Policy[];
};

function updatePolicy(builder: PoliciesBuilder, api: PoliciesBaseApi) {
    return builder.mutation<ApiResponse<API.PolicyViewModel>, UpdatePolicyPayload>({
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.policies.failedToUpdatePolicy")),
        query: async ({ id, content, ...payload }) => {
            const base = await RouteService.getPoliciesRoute();
            return {
                url: `${base}${id}`,
                method: "PUT",
                data: { ...payload, content },
            };
        },

        async onQueryStarted({ id, name, content }, { dispatch, queryFulfilled, getState }) {
            // The payload carries no workspace id, which is what keys the `getPolicies` cache, so every cached
            // workspace is visited and the ones not holding this policy are left untouched.
            const cachedArgs = api.util.selectCachedArgsForQuery(getState(), "getPolicies");

            const writeToCache = (update: Partial<API.PolicyViewModel>) => [
                ...cachedArgs.map((queryArg) =>
                    dispatch(
                        api.util.updateQueryData("getPolicies", queryArg, (draft) => {
                            const policy = draft.list.find((item) => item.id === id);
                            if (policy) {
                                Object.assign(policy, update);
                            }
                        }),
                    ),
                ),
                dispatch(
                    api.util.updateQueryData("getPolicyById", { id }, (draft) => {
                        if (draft.model) {
                            Object.assign(draft.model, update);
                        }
                    }),
                ),
            ];

            const patches = writeToCache({ name, content });

            try {
                const { data } = await queryFulfilled;
                // `hash` decides whether a device still complies with its policy, and only the server can compute
                // it, so a response without one has to be refetched.
                if (data.model?.hash) {
                    writeToCache(data.model);
                } else {
                    dispatch(api.util.invalidateTags(["Policies"]));
                }
            } catch {
                for (const patch of patches) {
                    patch.undo();
                }
            }
        },
    });
}

function deletePolicy(builder: PoliciesBuilder) {
    return builder.mutation<BasicApiResponse, PolicyIdParam>({
        invalidatesTags: invalidateOnSuccess("Policies"),
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.policies.failedToDeletePolicy")),
        query: async ({ id }) => {
            const base = await RouteService.getPoliciesRoute();
            return {
                url: `${base}${id}`,
                method: "DELETE",
            };
        },
    });
}

export type PolicyGroupsPayload = PolicyIdParam & {
    groupIds: number[];
};

function assignPolicyToGroups(builder: PoliciesBuilder) {
    return builder.mutation<ApiResponse<API.PolicyViewModel>, PolicyGroupsPayload>({
        invalidatesTags: invalidateOnSuccess("Policies"),
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.policies.failedToAssignPolicy")),
        query: async ({ id, groupIds }) => {
            const base = await RouteService.getPoliciesRoute();
            return {
                url: `${base}${id}/groups`,
                method: "POST",
                data: { groupIds },
            };
        },
    });
}

function unassignPolicyFromGroups(builder: PoliciesBuilder) {
    return builder.mutation<ApiResponse<API.PolicyViewModel>, PolicyGroupsPayload>({
        invalidatesTags: invalidateOnSuccess("Policies"),
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.policies.failedToUnassignPolicy")),
        query: async ({ id, groupIds }) => {
            const base = await RouteService.getPoliciesRoute();
            return {
                url: `${base}${id}/groups`,
                method: "DELETE",
                data: { groupIds },
            };
        },
    });
}

export default {
    getPolicies,
    getPolicyById,
    createPolicy,
    updatePolicy,
    deletePolicy,
    assignPolicyToGroups,
    unassignPolicyFromGroups,
};
